import get from 'lodash/get'
import * as React from 'react'
import { DropTarget } from 'react-dnd'
import { connect as reduxConnect } from 'react-redux'

import {
  TRACK_PLAYLISTED_FROM_SIM_SEARCH,
  TRACK_PLAYLISTED_FROM_SIM_TAB,
} from '@disco/constants/EventTypes'
import { INBOX_TYPES } from '@disco/constants/GroupTypes'
import { FILE, PLAYLIST, SECTION, TRACK } from '@disco/constants/ItemTypes'
import localProvider from '@disco/services/upload/providers/local'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import { resetSelection } from '@disco/state-management/actions/SelectionActions'
import {
  addShortlistSection,
  insertAggregatedTrack,
  insertExternalPlaylistTracks,
  insertExternalTrackBatch,
  insertItem,
  insertPlaylist,
  insertSelectedItems,
  moveSelectedItems,
  removeShortlistSection,
  updateShortlistSection,
} from '@disco/state-management/actions/ShortlistActions'
import {
  getGroups,
  getGroupsFromExternalShare,
  getTeamLibrary,
} from '@disco/state-management/selectors/groups'
import {
  getAllSelectedItems,
  getShortlistsSelection,
} from '@disco/state-management/selectors/selection'
import {
  getShortlistItems,
  getShortlistSections,
} from '@disco/state-management/selectors/shortlists'
import {
  sendPlaylistDownloadEventThrottled,
  sendTrackDownloadEventThrottled,
} from '@disco/utils/eventsApi'
import { getFilesFromDraggedItem } from '@disco/utils/file-utils'
import { getMixpanelDragAndDropProps } from '@disco/utils/mixpanel/drop-tracks'
import { pushUINotification } from '@disco/utils/notifications'
import { isDraggedItemPartOfSelection } from '@disco/utils/selection'
import {
  getIndexWithoutSections,
  getItemsWithSections,
} from '@disco/utils/shortlist'
import { isAggregatedSearchTrack } from '@disco/utils/tracks'

import type { ShortlistSection, ShortlistTrackItem } from 'types/shortlist'
import type { TrackId } from 'types/track'

const getDraggedTracks = (
  draggedItem: any,
  selectedItems: any[],
): TrackId[] => {
  let result: TrackId[] = []
  const partOfSelection = isDraggedItemPartOfSelection(
    selectedItems,
    draggedItem,
  )

  if (partOfSelection) {
    result = selectedItems.map(track => track.id)
  } else if (draggedItem.track) {
    result = [draggedItem.track.id]
  }

  return result
}

const getTargetSection = (
  index: number,
  items: (ShortlistTrackItem | ShortlistSection)[],
): ShortlistSection | undefined => {
  const normalizedIndex = Math.min(index, items.length - 1)
  const item = items[normalizedIndex]

  if (!item) {
    return undefined
  }

  if (!('track' in item)) {
    return item
  }

  return undefined
}

export const shortlistTarget = {
  drop(props, monitor, component) {
    const { items, sections, selectedItems } = props
    const draggedItem = monitor.getDropResult()
    // Sortable returns drop result, user can drop item outside of the sortable,
    // or to item that already handled the drop, in this case we won't do anything
    if (!draggedItem || draggedItem?.alreadyHandled) return props
    const draggedTracks = getDraggedTracks(draggedItem, selectedItems)

    const isNotDragged = (
      item: ShortlistTrackItem | ShortlistSection,
    ): boolean => {
      if ('track' in item && item.track) {
        return !draggedTracks.includes(item.track.id)
      }

      return item.id !== draggedItem.section?.id
    }
    const isSourceGroupExternal = originId =>
      props.externalSharedGroups.some(group => group.id === originId)

    const itemsWithSections = getItemsWithSections(items, sections).filter(
      isNotDragged,
    )
    const position = getIndexWithoutSections(
      draggedItem.index,
      itemsWithSections,
    )
    const targetSection = getTargetSection(
      draggedItem.index - 1,
      itemsWithSections,
    )

    if (draggedItem.section) {
      const { shortlistId } = props
      const { section, shortlistId: sourceShortlistId } = draggedItem

      if (props.shortlistId === sourceShortlistId) {
        // Convert combined-array index to sections-only index so the reducer
        // can correctly insert the section relative to other sections that share
        // the same track position.
        const sectionTargetIndex =
          draggedItem.index != null
            ? itemsWithSections
                .slice(0, draggedItem.index)
                .filter(item => !('track' in item)).length
            : undefined
        props.updateShortlistSection(
          shortlistId,
          section.id,
          section.name,
          position,
          sectionTargetIndex,
        )
      } else {
        props.addShortlistSection(shortlistId, section.name, position)
        props.removeShortlistSection(sourceShortlistId, section.id)
      }

      return
    }

    if (draggedItem.items) {
      getFilesFromDraggedItem(draggedItem).then((files: File[]) => {
        if (files.length > 0) {
          props.uploadFiles(
            localProvider,
            props.shortlistId,
            files,
            pushUINotification,
            {
              positionId: position,
              targetSection,
              wasDragged: true,
            },
          )
          props.sendMixpanelEvent('Playlist Creator - Drag and drop files', {
            Count: files.length,
          })
        }
      })
      return
    }

    const partOfSelection = isDraggedItemPartOfSelection(
      selectedItems,
      draggedItem,
    )

    const currentShortlistSelection = get(
      props.shortlistsSelection[props.shortlistId],
      'items',
      null,
    )
    const mixpanelProperties = getMixpanelDragAndDropProps(
      draggedItem,
      'Playlist creator',
    )
    if (currentShortlistSelection && currentShortlistSelection.length > 0) {
      // user actually moves items inside the same shortlist
      props.moveSelectedItems(
        props.shortlistId,
        currentShortlistSelection,
        position,
        targetSection,
      )
      props.resetSelection()
      return
    } else if (selectedItems.length > 0 && partOfSelection) {
      if (INBOX_TYPES.includes(draggedItem.sourceGroupType)) {
        selectedItems.forEach(item => {
          if (item.type === PLAYLIST) {
            sendPlaylistDownloadEventThrottled(
              item.playlist_id,
              undefined,
              draggedItem.sourceGroupId,
            )
          } else if (item.type === TRACK) {
            const trackId = item.id
            sendTrackDownloadEventThrottled(trackId, 'in-app', {
              group_id: draggedItem.sourceGroupId,
              playlist_id: draggedItem.sourcePlaylistId,
            })
          }
        })
      }

      if (isSourceGroupExternal(draggedItem.sourceGroupId)) {
        props.insertExternalTrackBatch(
          props.shortlistId,
          selectedItems,
          draggedItem.sourceGroupId,
          position,
          targetSection,
        )
      } else {
        props.insertSelectedItems(
          props.shortlistId,
          selectedItems,
          position,
          targetSection,
        )
      }
      props.resetSelection()

      props.sendMixpanelEvent(
        'Drag and drop selected items',
        mixpanelProperties,
      )
      return
    }

    if (draggedItem.track) {
      if (!draggedItem.track?.permissions?.manage) {
        props.insertAggregatedTrack(
          props.shortlistId,
          draggedItem.track,
          position,
          targetSection,
        )
      } else {
        props.insertItem(
          props.shortlistId,
          draggedItem,
          position,
          targetSection,
        )
      }

      const internal_data = draggedItem.track.internal_data

      props.sendMixpanelEvent(
        'Drag and drop existing track',
        mixpanelProperties,
        // Property for event #9 from https://disco-team.atlassian.net/browse/DISCO-5985
      )

      if (internal_data?.[TRACK_PLAYLISTED_FROM_SIM_TAB]) {
        props.sendMixpanelEvent(
          'Track playlisted from Track Panel Similar Tracks',
          {
            'Track panel': internal_data[TRACK_PLAYLISTED_FROM_SIM_TAB].panelId,
            'Track playlisted': draggedItem.track.id,
          },
        )
      }

      if (internal_data?.[TRACK_PLAYLISTED_FROM_SIM_SEARCH]) {
        props.sendMixpanelEvent(
          'Track playlisted from Similarity Search by URL',
          {
            ...mixpanelProperties,
            URL: internal_data[TRACK_PLAYLISTED_FROM_SIM_SEARCH].url,
            'Track playlisted': draggedItem.track.id,
          },
        )
      }

      if (INBOX_TYPES.includes(draggedItem.sourceGroupType)) {
        sendTrackDownloadEventThrottled(draggedItem.track.id, 'in-app', {
          group_id: draggedItem.sourceGroupId,
          playlist_id: draggedItem.sourcePlaylistId,
        })
      }
    }
    const isDiscoverItem = draggedItem?.id?.includes('discover')
    if (draggedItem.playlist && !isDiscoverItem) {
      if (!draggedItem?.playlist?.permissions?.manage) {
        props.insertExternalPlaylistTracks(
          props.shortlistId,
          draggedItem.playlist,
          position,
          targetSection,
          draggedItem.sourceGroupId,
        )
      } else {
        props.insertPlaylist(
          props.shortlistId,
          draggedItem,
          position,
          targetSection,
        )
      }
      props.sendMixpanelEvent('Drag and drop existing playlist')

      if (INBOX_TYPES.includes(draggedItem.sourceGroupType)) {
        sendPlaylistDownloadEventThrottled(
          draggedItem.playlist.playlist_id,
          undefined,
          draggedItem.sourceGroupId,
        )
      }
    }

    props.resetSelection()
  },
}

function collect(connect, monitor) {
  return {
    connectDropTarget: connect.dropTarget(),
  }
}

type Props = {
  shortlistId: string
  shortlistsSelection: Record<string, any>
  selectedItems: Record<string, any>[]
  uploadFiles: (...args: Array<any>) => any
  resetSelection: typeof resetSelection
  sendMixpanelEvent: typeof sendMixpanelEvent
  insertPlaylist: typeof insertPlaylist
  insertAggregatedTrack: typeof insertAggregatedTrack
  insertItem: typeof insertItem
  insertSelectedItems: typeof insertSelectedItems
  moveSelectedItems: typeof moveSelectedItems
  addShortlistSection: typeof addShortlistSection
  removeShortlistSection: typeof removeShortlistSection
  updateShortlistSection: typeof updateShortlistSection
  insertExternalPlaylistTracks: typeof insertExternalPlaylistTracks
  connectDropTarget: (...args: Array<any>) => any
  children: React.ReactNode
}

class ShortlistDropWrapper extends React.Component<Props> {
  render() {
    const { connectDropTarget } = this.props
    return connectDropTarget(<div>{this.props.children}</div>)
  }
}

const dropWrapper = DropTarget(
  [FILE, TRACK, PLAYLIST, SECTION],
  shortlistTarget,
  collect,
)(ShortlistDropWrapper)
export default reduxConnect(
  (state, props: { shortlistId: string }) => ({
    groups: getGroups(state),
    externalSharedGroups: getGroupsFromExternalShare(state),
    items: getShortlistItems(state, props),
    sections: getShortlistSections(state, props),
    selectedItems: getAllSelectedItems(state),
    shortlistsSelection: getShortlistsSelection(state),
    teamLibrary: getTeamLibrary(state),
  }),
  {
    insertPlaylist,
    insertAggregatedTrack,
    insertItem,
    insertSelectedItems,
    moveSelectedItems,
    resetSelection,
    sendMixpanelEvent,
    addShortlistSection,
    removeShortlistSection,
    updateShortlistSection,
    insertExternalPlaylistTracks,
    insertExternalTrackBatch,
  },
)(dropWrapper) as any
